// Single seam for deciding which Solution.attributes (raw BoardMembers sheet columns, e.g.
// "good_for") a visitor sees and how they read. Shared by the embed's result screen
// (src/presentation/result-screen.ts) and the downloadable report (src/session/report-template.ts)
// so the two never drift apart. Same "shared seam" convention as src/booking-url.ts.
//
// Other columns are matching/internal inputs and stay off the visitor's screen: only the
// allowlist below is rendered, in this order.

const DISPLAYED_ATTRIBUTES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "good_for", label: "Good For" },
  { key: "style", label: "Style" },
  { key: "years_experience", label: "Years Experience" },
  { key: "fits_business_stage", label: "Fits Business Stage" },
  { key: "brings", label: "Brings" },
];

function normalizeKey(key: string): string {
  return key.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/** Sheet values are often stored slug-style ("high-end-residential"). When a value is entirely
 * lower case, turn dashes/underscores into spaces and capitalise the first letter; a value that
 * already has mixed case was written by a human, so it is left alone. */
export function attributeValue(value: string): string {
  const trimmed = value.trim();
  if (trimmed !== trimmed.toLowerCase()) return trimmed;
  const spaced = trimmed.replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The visitor-facing [label, value] pairs for a Board Member: allowlisted attributes only, in
 * allowlist order, skipping empty values. */
export function displayAttributes(attributes?: Record<string, string | undefined>): Array<[string, string]> {
  const byKey = new Map<string, string>();
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (value && value.trim()) byKey.set(normalizeKey(key), value);
  }
  const out: Array<[string, string]> = [];
  for (const { key, label } of DISPLAYED_ATTRIBUTES) {
    const value = byKey.get(key);
    if (value) out.push([label, attributeValue(value)]);
  }
  return out;
}
